import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { changeUserPassword } from "../../../utils/apis";
import formikTextFieldProps from "./formikTextFieldProps";

// same rule the API enforces (8+ chars, upper, lower, digit and one of #?!@$%^&*-)
const STRONG_PASSWORD = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/;

const validationSchema = Yup.object().shape({
  newPassword: Yup.string()
    .required("Password is required")
    .max(50, "Password can be at most 50 characters")
    .matches(
      STRONG_PASSWORD,
      "Use at least 8 characters with an uppercase letter, a lowercase letter, a number and a symbol (#?!@$%^&*-)"
    ),
  confirmPassword: Yup.string()
    .required("Confirm the password")
    .oneOf([Yup.ref("newPassword")], "Passwords do not match"),
});

const ChangePasswordDialog = ({ user, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState(false);

  const formik = useFormik({
    initialValues: { newPassword: "", confirmPassword: "" },
    validationSchema,
    onSubmit: async ({ newPassword }) => {
      const res = await changeUserPassword(user._id, newPassword, enqueueSnackbar);
      if (res !== false) onClose();
    },
  });

  const field = (name, label, props = {}) => (
    <TextField
      id={`change-password-${name}`}
      label={label}
      type={showPassword ? "text" : "password"}
      fullWidth
      required
      autoComplete="new-password"
      {...formikTextFieldProps(formik, name)}
      {...props}
    />
  );

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Change password</DialogTitle>
      <form onSubmit={formik.handleSubmit} noValidate>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Set a new password for{" "}
            <strong>
              {[user.firstName, user.lastName].filter(Boolean).join(" ") || user.email}
            </strong>
            . They can sign in with it straight away.
          </DialogContentText>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {field("newPassword", "New password", {
              autoFocus: true,
              InputProps: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? "Hide passwords" : "Show passwords"}
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            })}
            {field("confirmPassword", "Confirm password")}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={formik.isSubmitting}>
            Update password
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ChangePasswordDialog;
